import React from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import { PEOPLE, ECOSYSTEM } from "@/lib/content";

const WhoWeAre = () => {
  return (
    <>
      <PageHeader
        eyebrow="Who we are"
        title={`Two co-founders. One managed process.`}
        subtitle={`Bright EOR is co-founded by operators who've built and run teams across the UK and India — not a faceless platform, not a broker.`}
      />

      <section className="py-20 lg:py-28" data-testid="who-founder-story">
        <div className="mx-auto max-w-5xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Founder-led</p>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-[1.08]">
              Two co-founders at the front. Dedicated teams behind us.
            </h2>
            <p className="mt-5 text-lg text-slateink leading-relaxed">
              Bright EOR is co-founded by <strong className="text-navy">Richard Rozalewicz</strong> (Founder of Brightridge Partners) and <strong className="text-navy">Kevin C.</strong> (Co-Founder of Brightridge Recruitment). We both operate across the UK and India as the business requires, and lead the client relationship personally.
            </p>
            <p className="mt-4 text-lg text-slateink leading-relaxed">
              Behind us sit dedicated teams supporting every step of the process — sourcing, screening, employment setup, IT onboarding, HR administration, payroll and ongoing account management — so the setup around your India-based hire is properly structured from day one.
            </p>
            <p className="mt-4 text-lg text-slateink leading-relaxed">
              Between us, we combine UK recruitment DNA, business-owner expectations, and hands-on operational experience across both markets. That combination is exactly what Bright EOR is built on.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-navy-50 py-20 lg:py-28" data-testid="who-team">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">The co-founders</p>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-[1.08]">
              Founder-led, UK &amp; India.
            </h2>
            <p className="mt-5 text-lg text-slateink leading-relaxed">
              You deal directly with the co-founders. Dedicated teams support every step behind the scenes.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {PEOPLE.map((p) => (
              <div
                key={p.name}
                className="rounded-3xl bg-white border border-slate-200 overflow-hidden shadow-card-soft flex flex-col"
                data-testid={`who-person-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <PersonPortrait person={p} />
                <div className="p-8 lg:p-10">
                  <h3 className="font-display text-2xl font-semibold text-navy tracking-tight">{p.name}</h3>
                  <p className="mt-1 text-sm text-aqua font-semibold tracking-tight">{p.role}</p>
                  {p.subrole && (
                    <p className="text-sm text-slatemuted tracking-tight">{p.subrole}</p>
                  )}
                  <div className="mt-6 space-y-4">
                    {p.bio.map((line, i) => (
                      <p key={i} className="text-[15px] text-slateink leading-relaxed">{line}</p>
                    ))}
                  </div>
                  {p.links.length > 0 && (
                    <div className="mt-7 flex flex-wrap gap-2">
                      {p.links.map((l) => (
                        <a
                          key={l.href}
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:border-aqua hover:text-aqua transition-colors"
                          data-testid={`who-person-link-${l.label.toLowerCase()}`}
                        >
                          {l.label}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="who-ecosystem">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">The group</p>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold text-navy tracking-tight leading-[1.08]">
              Part of Brightridge Partners.
            </h2>
            <p className="mt-5 text-lg text-slateink leading-relaxed">
              Bright EOR sits under Brightridge Partners alongside specialist service brands built to help UK businesses hire, grow and operate smarter.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {ECOSYSTEM.map((b) => {
              const inner = (
                <>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${b.current ? "bg-aqua text-navy" : "bg-aqua/15 text-navy"}`}>
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <h3 className={`font-display mt-6 text-xl font-semibold tracking-tight ${b.current ? "text-white" : "text-navy"}`}>{b.name}</h3>
                  <p className={`mt-2 text-[14px] leading-relaxed ${b.current ? "text-white/75" : "text-slateink"}`}>{b.body}</p>
                  {b.current ? (
                    <p className="mt-6 text-xs uppercase tracking-[0.22em] text-aqua font-semibold">You are here</p>
                  ) : (
                    <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-navy group-hover:text-aqua transition-colors">
                      Visit site <ExternalLink className="h-3 w-3" />
                    </span>
                  )}
                </>
              );
              const base = `group rounded-3xl p-8 border transition-all ${
                b.current ? "bg-navy text-white border-navy shadow-card-hover" : "bg-white border-slate-200 hover:border-aqua/40 hover:shadow-card-hover"
              }`;
              return b.url ? (
                <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className={base} data-testid={`who-ecosystem-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {inner}
                </a>
              ) : (
                <div key={b.name} className={base} data-testid={`who-ecosystem-${b.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

/*
 * Portrait treatment (post background-swap):
 * Source images now ship with a navy + aqua brand backdrop baked in, so the
 * frame is intentionally minimal — a rounded top of the card, a subtle
 * CO-FOUNDER pill overlay and a soft fade-out at the bottom to blend into the
 * white card body.
 */
const PersonPortrait = ({ person }) => {
  if (!person.image) {
    return (
      <div className="h-56 bg-navy flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <span className="relative font-display text-5xl font-semibold text-aqua tracking-tight">{person.initials}</span>
      </div>
    );
  }
  return (
    <div className="relative aspect-[4/3] bg-navy overflow-hidden">
      <img
        src={person.image}
        alt={person.name}
        className="relative h-full w-full object-cover"
        style={{ objectPosition: "center 20%" }}
      />
      {/* Very subtle fade at the bottom to blend into the white card body */}
      <div
        className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(11,29,52,0), rgba(11,29,52,0.35))" }}
        aria-hidden
      />
      <div className="absolute bottom-4 left-6">
        <span className="rounded-full bg-aqua text-navy text-[10px] font-semibold tracking-[0.14em] uppercase px-2.5 py-1">
          Co-Founder
        </span>
      </div>
    </div>
  );
};

export default WhoWeAre;
